/**
 * Replicate API Service
 * 
 * Handles floor plan inpainting using SDXL via Replicate.
 * Uses Supabase Edge Function as a proxy.
 */

// ============================================================================
// TYPES
// ============================================================================

export interface InpaintSelection {
  /** X position as percentage (0-1) */
  x: number;
  /** Y position as percentage (0-1) */
  y: number;
  /** Width as percentage (0-1) */
  width: number;
  /** Height as percentage (0-1) */
  height: number;
}

export interface ReplicateInpaintResult {
  success: boolean;
  imageUrl?: string;
  error?: string;
  predictionId?: string;
}

// ============================================================================
// MASK GENERATION
// ============================================================================

/**
 * Creates a black/white mask image from selection coordinates.
 * White = area to edit, Black = area to preserve
 * 
 * @param imageUrl - The original image URL to get dimensions from
 * @param selection - The selection box (normalized 0-1 coordinates)
 * @returns Base64 data URL of the mask image
 */
export async function createMaskFromSelection(
  imageUrl: string,
  selection: InpaintSelection
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        reject(new Error('Failed to create canvas context'));
        return;
      }
      
      // Match the image dimensions
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      
      // Fill with black (preserve area)
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw white rectangle for edit area
      const x = Math.round(selection.x * canvas.width);
      const y = Math.round(selection.y * canvas.height);
      const w = Math.round(selection.width * canvas.width);
      const h = Math.round(selection.height * canvas.height);
      
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(x, y, w, h);
      
      // Convert to base64
      const maskBase64 = canvas.toDataURL('image/png');
      
      console.log('='.repeat(60));
      console.log('MASK GENERATED');
      console.log('Image dimensions:', canvas.width, 'x', canvas.height);
      console.log('Selection box (px):', { x, y, w, h });
      console.log('='.repeat(60));
      
      resolve(maskBase64);
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image for mask generation'));
    };
    
    img.src = imageUrl;
  });
}

// ============================================================================
// REPLICATE INPAINTING
// ============================================================================

/**
 * Inpaint a floor plan image using Replicate SDXL.
 * 
 * @param imageUrl - The original image URL or base64
 * @param selection - The selection box to edit
 * @param prompt - The edit prompt (e.g., "add door symbol")
 * @param supabaseUrl - Supabase project URL for Edge Function
 * @param supabaseAnonKey - Supabase anon key for auth
 */
export async function inpaintWithReplicate(
  imageUrl: string,
  selection: InpaintSelection,
  prompt: string,
  supabaseUrl: string,
  supabaseAnonKey: string
): Promise<ReplicateInpaintResult> {
  console.log('='.repeat(60));
  console.log('REPLICATE INPAINT REQUEST');
  console.log('Selection:', selection);
  console.log('Prompt:', prompt);
  console.log('='.repeat(60));

  try {
    // Step 1: Generate the mask from selection
    const maskBase64 = await createMaskFromSelection(imageUrl, selection);
    
    // Step 2: Get the image as base64 if it's a URL
    let imageBase64: string;
    if (imageUrl.startsWith('data:')) {
      imageBase64 = imageUrl;
    } else {
      // Fetch and convert to base64
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      imageBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    }
    
    // Step 3: Call the Supabase Edge Function
    // The function slug in Supabase is 'clever-processor' (auto-generated)
    const edgeFunctionUrl = `${supabaseUrl}/functions/v1/clever-processor`;
    
    console.log('Calling Edge Function:', edgeFunctionUrl);
    
    const response = await fetch(edgeFunctionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify({
        image: imageBase64,
        mask: maskBase64,
        prompt: prompt,
      }),
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      console.error('Edge Function error:', result);
      return {
        success: false,
        error: result.error || 'Inpainting failed',
      };
    }
    
    console.log('Replicate inpaint successful:', result);
    
    return {
      success: true,
      imageUrl: result.imageUrl,
      predictionId: result.predictionId,
    };
    
  } catch (error: any) {
    console.error('Replicate inpaint error:', error);
    return {
      success: false,
      error: error.message || 'Inpainting failed',
    };
  }
}

// ============================================================================
// SIMPLE PROMPT MAPPING
// Short, focused prompts - Edge Function adds the style prefix/suffix
// ============================================================================

/**
 * Maps amendment action types to short, focused prompts.
 * The Edge Function wraps these with architectural style guardrails.
 */
export const AMENDMENT_PROMPTS: Record<string, string> = {
  addDoor: 'Add single swing door symbol',
  addWindow: 'Add window symbol, double glazed line',
  removeWall: 'Remove object, white floor space, empty',
  buildWall: 'Solid black wall connection',
  flipSwing: 'Flip door swing arc direction',
  clearObject: 'Remove object, white floor space, empty',
};

/**
 * Get the prompt for an amendment action.
 * Returns the short prompt that will be wrapped by Edge Function.
 */
export function getAmendmentPrompt(actionType: string, customPrompt?: string): string {
  if (customPrompt) {
    return customPrompt;
  }
  return AMENDMENT_PROMPTS[actionType] || 'clean up this area';
}
